import { useState, useCallback } from 'react';
import '../../assets/css/case-property.css';
import cornerIcon from '../../assets/images/case-image-corner.svg';
// @ts-ignore
import { useCollectionBinding, useSlotGroup, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
// @ts-ignore
import type { ComponentContentSchema, CollectionBindingDefinition, SlotGroupDefinition } from '@vibesites/runtime';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck } from '@fortawesome/free-solid-svg-icons';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { PhotoLightbox } from './PhotoLightbox';
import { useFilteredGalleryIndices } from './useFilteredGallery';
import { openTourScheduler } from './TourSchedulerModal';

/**
 * Slot group definitions for amenities page photos (bound to photo_gallery[0..3]).
 * Uses amenitieslist. prefix per schema linter; photos passed to PhotoLightbox for lightbox.
 */
function photoSlotDef(index: number): SlotGroupDefinition {
  return {
    label: `Photo ${index + 1}`,
    fields: {
      image: {
        label: 'Image',
        type: 'image',
        defaultField: `photo_gallery[${index}].image`,
      },
      alt: {
        label: 'Alt Text',
        type: 'text',
        defaultField: `photo_gallery[${index}].alt_text`,
      },
    },
    sourceBinding: {
      collection: 'photo_gallery',
      defaultIndex: index,
      fieldMappings: { image: 'image', alt: 'alt_text' },
    },
  };
}

const AMENITIES_PHOTO_SLOT_DEFS: Record<string, SlotGroupDefinition> = {};
for (let i = 0; i < 4; i++) {
  AMENITIES_PHOTO_SLOT_DEFS[`amenitieslist.photo_${i}`] = photoSlotDef(i);
}

const COMMUNITY_BLURB_DEFAULT =
  "<p>Throughout our luxury apartment community, you'll find amenities designed to suit your needs, allowing you to make the most of every day.</p>";
const APARTMENT_BLURB_DEFAULT =
  "<p>Start each day right by waking up in one of our gorgeous <span data-type=\"field-token\" data-field=\"property_address_city\"></span> apartment homes.</p>";

/**
 * Links specific amenity names to partner sites.
 */
import flexLogo from '../../assets/images/flex_logo.webp';
import coxLogo from '../../assets/images/cox_logo.webp';
import spectrumLogo from '../../assets/images/spectrum_logo.webp';

const AMENITY_LINKS: { pattern: RegExp; href: string; logo: string; logoAlt: string }[] = [
  { pattern: /flex/i, href: 'https://getflex.com/', logo: flexLogo, logoAlt: 'Flex' },
  { pattern: /cox/i, href: 'https://www.cox.com/quickconnect', logo: coxLogo, logoAlt: 'Cox' },
  { pattern: /spectrum/i, href: 'https://spectrum.com/quickconnect', logo: spectrumLogo, logoAlt: 'Spectrum' },
];

function renderAmenityText(item: string) {
  const link = AMENITY_LINKS.find((l) => l.pattern.test(item));
  if (link) {
    return (
      <a href={link.href} target="_blank" rel="noopener noreferrer" className="prop-al-amenity-link">
        {item}
        <img src={link.logo} alt={link.logoAlt} className="prop-al-amenity-logo" />
      </a>
    );
  }
  return <span>{item}</span>;
}

/**
 * Full amenities page matching the Stratman/CaseUSA amenities layout.
 * Two-column intro → Community amenities (dark, 2-col) → Apartment amenities (light, 2-col) → Resource links.
 */
export function AmenitiesList() {
  const communityAmenities: any[] = useCollectionBinding('amenitieslist.community', {
    collection: 'amenities' as any,
    filter: { group: 'Community' },
  });
  const apartmentAmenities: any[] = useCollectionBinding('amenitieslist.apartment', {
    collection: 'amenities' as any,
    filter: { group: 'Apartment' },
  });

  const communityItems = communityAmenities.map((a: any) => a.title).filter(Boolean);
  const apartmentItems = apartmentAmenities.map((a: any) => a.title).filter(Boolean);
  const hasAsterisk = apartmentItems.some((a: string) => a.includes('*'));

  // Resolve photos from slot groups, skipping Logo/Other/Floorplan titles
  const fi = useFilteredGalleryIndices();
  const photo0 = useSlotGroup('amenitieslist.photo_0', photoSlotDef(fi[0] ?? 0));
  const photo1 = useSlotGroup('amenitieslist.photo_1', photoSlotDef(fi[1] ?? 1));
  const photo2 = useSlotGroup('amenitieslist.photo_2', photoSlotDef(fi[2] ?? 2));
  const photo3 = useSlotGroup('amenitieslist.photo_3', photoSlotDef(fi[3] ?? 3));
  const photoSlots = [photo0, photo1, photo2, photo3];
  const communitySlots = photoSlots.slice(0, 3).filter(s => s.image);
  const apartmentSlot = photo3.image ? photo3 : photo0;

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const openLightbox = useCallback((slotIndex: number) => {
    setLightboxIndex(slotIndex);
    setLightboxOpen(true);
  }, []);

  return (
    <>
      {/* ── Community Amenities (dark bg, two-column: photos left, text right) ── */}
      <section className="prop-al-content-cols prop-al-bg-dark">
        <div className="prop-al-container-main">
          <div className="prop-al-cols-row reverse">
            <div className="prop-al-col prop-al-images-col" style={{ position: 'relative' }}>
              <img src={cornerIcon} alt="" className="prop-corner-icon prop-corner-icon--tl" aria-hidden="true" />
              <div className="prop-al-images-box">
                {communitySlots.length > 0 && (
                  <div className="prop-al-photo-grid secondary">
                    {communitySlots[0] && (
                      <EditableSlotGroup groupPath="amenitieslist.photo_0" definition={AMENITIES_PHOTO_SLOT_DEFS['amenitieslist.photo_0']} className="prop-al-photo-item">
                        <button type="button" className="prop-al-photo-item-inner" onClick={() => openLightbox(0)}>
                          <VibesitesImage url={communitySlots[0].image!} alt={communitySlots[0].alt || ''} priority sizes="(min-width: 992px) 50vw, 100vw" />
                        </button>
                      </EditableSlotGroup>
                    )}
                    {communitySlots[1] && (
                      <EditableSlotGroup groupPath="amenitieslist.photo_1" definition={AMENITIES_PHOTO_SLOT_DEFS['amenitieslist.photo_1']} className="prop-al-photo-item">
                        <button type="button" className="prop-al-photo-item-inner" onClick={() => openLightbox(1)}>
                          <VibesitesImage url={communitySlots[1].image!} alt={communitySlots[1].alt || ''} sizes="(min-width: 992px) 25vw, 50vw" />
                        </button>
                      </EditableSlotGroup>
                    )}
                    {communitySlots[2] && (
                      <EditableSlotGroup groupPath="amenitieslist.photo_2" definition={AMENITIES_PHOTO_SLOT_DEFS['amenitieslist.photo_2']} className="prop-al-photo-item">
                        <button type="button" className="prop-al-photo-item-inner" onClick={() => openLightbox(2)}>
                          <VibesitesImage url={communitySlots[2].image!} alt={communitySlots[2].alt || ''} sizes="(min-width: 992px) 25vw, 50vw" />
                        </button>
                      </EditableSlotGroup>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="prop-al-col">
              <div className="prop-al-text-box">
                <RichTextContent path="amenitieslist.community_title" label="Community Title" defaultValue={contentSchema.tokens['amenitieslist.community_title'].defaultValue} as="h2" className="prop-al-section-title" style={{ fontSize: '2.25rem', fontWeight: 700 }} />
                <div className="prop-al-separator" />

                <RichTextContent path="amenitieslist.community_blurb" label="Community Description" defaultValue={COMMUNITY_BLURB_DEFAULT} as="div" className="prop-al-blurb" />

                {communityItems.length > 0 && (
                  <ul className="prop-al-checklist">
                    {communityItems.map((item: string, i: number) => (
                      <li key={i}>
                        <FontAwesomeIcon icon={faCircleCheck} className="prop-al-check-icon" />
                        {renderAmenityText(item)}
                      </li>
                    ))}
                  </ul>
                )}

                <button type="button" className="prop-btn" onClick={openTourScheduler} data-vibesite-event="schedule_tour_clicked">Schedule a Tour</button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Apartment Amenities (light bg, two-column: text left in card, image right) ── */}
      <section className="prop-al-content-cols prop-al-bg-light">
        <div className="prop-al-container-main">
          <div className="prop-al-cols-row">
            <div className="prop-al-col">
              <div className="prop-al-text-box">
                <RichTextContent path="amenitieslist.apartment_title" label="Apartment Title" defaultValue={contentSchema.tokens['amenitieslist.apartment_title'].defaultValue} as="h2" className="prop-al-section-title" style={{ fontSize: '2.25rem', fontWeight: 700 }} />
                <div className="prop-al-separator" />

                <RichTextContent path="amenitieslist.apartment_blurb" label="Apartment Description" defaultValue={APARTMENT_BLURB_DEFAULT} as="div" className="prop-al-blurb" />

                {apartmentItems.length > 0 && (
                  <ul className="prop-al-checklist">
                    {apartmentItems.map((item: string, i: number) => (
                      <li key={i}>
                        <FontAwesomeIcon icon={faCircleCheck} className="prop-al-check-icon" />
                        {renderAmenityText(item)}
                      </li>
                    ))}
                  </ul>
                )}

                {hasAsterisk && (
                  <p className="prop-al-disclaimer"><em>*In select homes</em></p>
                )}

                <button type="button" className="prop-btn" onClick={openTourScheduler} data-vibesite-event="schedule_tour_clicked">Schedule a Tour</button>
              </div>
            </div>

            <div className="prop-al-col prop-al-images-col" style={{ position: 'relative' }}>
              <img src={cornerIcon} alt="" className="prop-corner-icon prop-corner-icon--tr" aria-hidden="true" />
              <EditableSlotGroup
                groupPath="amenitieslist.photo_3"
                definition={AMENITIES_PHOTO_SLOT_DEFS['amenitieslist.photo_3']}
                className="prop-al-images-box prop-al-single-image"
              >
                {apartmentSlot.image && (
                  <button type="button" className="prop-al-single-image-inner" onClick={() => openLightbox(3)}>
                    <VibesitesImage url={apartmentSlot.image} alt={apartmentSlot.alt || ''} sizes="(min-width: 992px) 50vw, 100vw" />
                  </button>
                )}
              </EditableSlotGroup>
            </div>
          </div>
        </div>
      </section>

      <PhotoLightbox
        photos={[photo0, photo1, photo2, photo3].map(s =>
          s.image ? { src: s.image, alt: s.alt || '' } : { src: 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="1" height="1"%3E%3C/svg%3E', alt: '' }
        )}
        initialIndex={lightboxIndex}
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'AmenitiesList',
  displayName: 'Amenities Full Page',
  tokens: {
    'amenitieslist.community_title': {
      label: 'Community Amenities Title',
      type: 'richtext',
      defaultValue: 'Community Amenities at <span data-type="field-token" data-field="property_name"></span>',
    },
    'amenitieslist.community_blurb': {
      label: 'Community Description',
      type: 'richtext',
      defaultValue: "<p>Throughout our luxury apartment community, you'll find amenities designed to suit your needs, allowing you to make the most of every day.</p>",
    },
    'amenitieslist.apartment_title': {
      label: 'Apartment Amenities Title',
      type: 'richtext',
      defaultValue: 'Apartment Amenities in <span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span>',
    },
    'amenitieslist.apartment_blurb': {
      label: 'Apartment Description',
      type: 'richtext',
      defaultValue: "<p>Start each day right by waking up in one of our gorgeous <span data-type=\"field-token\" data-field=\"property_address_city\"></span> apartment homes.</p>",
    },
  },
  slotGroups: {
    'amenitieslist.photo_0': {
      label: 'Photo 1',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text' },
      },
      sourceBinding: { collection: 'photo_gallery', defaultIndex: 0, fieldMappings: { image: 'image', alt: 'alt_text' } },
    },
    'amenitieslist.photo_1': {
      label: 'Photo 2',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[1].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[1].alt_text' },
      },
      sourceBinding: { collection: 'photo_gallery', defaultIndex: 1, fieldMappings: { image: 'image', alt: 'alt_text' } },
    },
    'amenitieslist.photo_2': {
      label: 'Photo 3',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[2].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[2].alt_text' },
      },
      sourceBinding: { collection: 'photo_gallery', defaultIndex: 2, fieldMappings: { image: 'image', alt: 'alt_text' } },
    },
    'amenitieslist.photo_3': {
      label: 'Photo 4',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[3].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[3].alt_text' },
      },
      sourceBinding: { collection: 'photo_gallery', defaultIndex: 3, fieldMappings: { image: 'image', alt: 'alt_text' } },
    },
  },
  collectionBindings: {
    'amenitieslist.community': {
      label: 'Community Amenities',
      collection: 'amenities',
      description: 'Community amenity items',
      filter: { group: 'Community' },
    } as CollectionBindingDefinition,
    'amenitieslist.apartment': {
      label: 'Apartment Amenities',
      collection: 'amenities',
      description: 'Apartment amenity items',
      filter: { group: 'Apartment' },
    } as CollectionBindingDefinition,
  },
};

(AmenitiesList as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default AmenitiesList;
